/* eslint-disable react-hooks/exhaustive-deps */
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { useFormikContext } from "formik";
import { useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";

import {
  CountryWithCode,
  EmployerCategories,
  StateWithCode
} from "../../../../config/filingConstants";
import { EToastvariant, IAddIncomeForm } from "../../../../config/interfaces";
import { useAppDispatch } from "../../../../redux/Store";
import { showHintModal } from "../../../../redux/slice/HintModalSlice";
import { showToastMsg } from "../../../../redux/slice/ToastMsgSlice";
import { UtilService } from "../../../../services";
import FASvgIcon from "../../../FASvgIcon";
import { FormField, NumericInputField, SelectInputField, TextInputField } from "../../../fields";

const SalaryEmployerDetailsForm: React.FC = () => {
  const { values, errors, touched, setFieldValue, handleBlur } = useFormikContext<IAddIncomeForm>();

  const dispatch = useAppDispatch();
  const [tempPincode, setTempPincode] = useState(values?.PinCode || "");

  useEffect(() => {
    if (tempPincode.length === 6) {
      getPincodeDetails();
    }
  }, [tempPincode]);

  const getPincodeDetails = async () => {
    try {
      setFieldValue("PinCode", Number(tempPincode));
      setFieldValue("ZipCode", tempPincode);
      const data = await UtilService.getStateCityFromPincode(tempPincode);
      if (data) {
        setFieldValue("CountryCode", data.CountryCode);
        setFieldValue("StateCode", data.StateCode);
        setFieldValue("CityOrTownOrDistrict", data.CityOrTownOrDistrict);
      } else {
        setFieldValue("CountryCode", "");
        setFieldValue("StateCode", "");
        setFieldValue("CityOrTownOrDistrict", "");
      }
    } catch (error) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    }
  };

  return (
    <section className="mb-3">
      <p className="leading-para fw-bold text-primary">Employer & TDS Details</p>

      <FormField label="Employer Name" isRequired={true} sm={4}>
        <TextInputField
          name="EmployerName"
          value={values.EmployerName}
          onBlur={(e) => {
            handleBlur(e);
            setFieldValue("EmployerName", values.EmployerName.trim());
          }}
          onChange={(value) => {
            setFieldValue("EmployerName", value);
          }}
          isInvalid={Boolean(errors.EmployerName) && touched.EmployerName}
          errorMsg={errors.EmployerName}
        />
      </FormField>

      <FormField label="Employer Category" isRequired={true} sm={4}>
        <SelectInputField
          placeholder="Select category"
          name="EmployerCategory"
          options={UtilService.convertToArray(EmployerCategories)}
          value={{
            "label": EmployerCategories[values.EmployerCategory],
            "value": values.EmployerCategory
          }}
          onChange={(selectedStatus) => {
            if (selectedStatus) {
              setFieldValue("EmployerCategory", selectedStatus.value);
              setFieldValue("EntertainmentAllowance16ii", "");
            } else {
              setFieldValue("EmployerCategory", undefined);
            }
          }}
          onBlur={handleBlur("EmployerCategory")}
          isInvalid={Boolean(errors.EmployerCategory) && touched.EmployerCategory}
          errorMsg={errors.EmployerCategory}
        />
      </FormField>

      <FormField
        label={
          <>
            TAN of the Employer
            <span
              className="info-wrap"
              role="button"
              onClick={() =>
                dispatch(
                  showHintModal({
                    title: "TAN of the Employer",
                    componentName: "EmployerTANHint"
                  })
                )
              }
            >
              <FASvgIcon icon={faInfoCircle} />
            </span>
          </>
        }
        isRequired={true}
        sm={4}
      >
        <TextInputField
          name="EmployerTAN"
          value={values.EmployerTAN}
          onChange={(value) => setFieldValue("EmployerTAN", value.toUpperCase())}
          onBlur={(e) => {
            handleBlur(e);
            setFieldValue("EmployerTAN", values.EmployerTAN.trim());
          }}
          isInvalid={Boolean(errors.EmployerTAN) && touched.EmployerTAN}
          errorMsg={errors.EmployerTAN}
        />
      </FormField>

      <FormField
        label={
          <>
            Total Tax Deducted at Source
            <span
              className="info-wrap"
              role="button"
              onClick={() =>
                dispatch(
                  showHintModal({
                    title: "Total Tax Deducted at Source (TDS)",
                    componentName: "TotalTDSHint"
                  })
                )
              }
            >
              <FASvgIcon icon={faInfoCircle} />
            </span>
          </>
        }
        isRequired={true}
        sm={4}
      >
        <NumericInputField
          showLeftIcon={true}
          placeholder=""
          name="TotalTDSSal"
          value={values.TotalTDSSal}
          onChange={(value) => setFieldValue("TotalTDSSal", value)}
          onBlur={handleBlur("TotalTDSSal")}
          isInvalid={Boolean(errors.TotalTDSSal) && touched.TotalTDSSal}
          errorMsg={errors.TotalTDSSal}
        />
      </FormField>

      <FormField label="Address of Employer" isRequired={true} sm={4}>
        <TextInputField
          name="EmployerAddress"
          value={values.EmployerAddress}
          onChange={(value) => setFieldValue("EmployerAddress", value)}
          onBlur={(e) => {
            handleBlur(e);
            setFieldValue("EmployerAddress", values.EmployerAddress.trim());
          }}
          isInvalid={Boolean(errors.EmployerAddress) && touched.EmployerAddress}
          errorMsg={errors.EmployerAddress}
        />
      </FormField>

      <FormField label="Employer Pincode" isRequired={true} sm={4}>
        <NumericInputField
          decimalUpto={0}
          separator=""
          name="PinCode"
          value={values?.PinCode || ""}
          onChange={async (value) => {
            setTempPincode(() => value);
            setFieldValue("PinCode", Number(value));
          }}
          onBlur={handleBlur("PinCode")}
          isInvalid={Boolean(errors.PinCode) && touched.PinCode}
          errorMsg={errors.PinCode}
        />
      </FormField>

      <Row className="align-items-center">
        <Col sm={4}>
          <Form.Label className="required-field mb-0">Country | State | City</Form.Label>
        </Col>
        <Col>
          <Row className="align-items-center">
            <Col sm={4}>
              <SelectInputField
                placeholder="Select Country"
                label="Country"
                name="CountryCode"
                isSearchable={true}
                isOptionArrayOfObject={true}
                options={UtilService.convertToArray(CountryWithCode)}
                value={{
                  "label": CountryWithCode[values.CountryCode],
                  "value": values.CountryCode
                }}
                onChange={(selectedCountry) => {
                  if (selectedCountry) {
                    setFieldValue("CountryCode", selectedCountry.value);
                    if (selectedCountry.value !== "91") {
                      setFieldValue("StateCode", "99");
                    } else {
                      setFieldValue("StateCode", "");
                    }
                  } else {
                    setFieldValue("CountryCode", undefined);
                  }
                }}
                onBlur={handleBlur("CountryCode")}
                isInvalid={Boolean(errors.CountryCode) && touched.CountryCode}
                errorMsg={errors.CountryCode}
              />
            </Col>
            <Col sm={4}>
              <SelectInputField
                placeholder="Select state"
                name="StateCode"
                label="State"
                isSearchable={true}
                isOptionArrayOfObject={true}
                options={UtilService.convertToArray(StateWithCode)}
                value={{
                  "label": StateWithCode[values.StateCode],
                  "value": values.StateCode
                }}
                onChange={(selectedState) => {
                  if (selectedState) {
                    setFieldValue("StateCode", selectedState.value);
                  } else {
                    setFieldValue("StateCode", undefined);
                  }
                }}
                onBlur={handleBlur("StateCode")}
                isInvalid={Boolean(errors.StateCode) && touched.StateCode}
                errorMsg={errors.StateCode}
              />
            </Col>
            <Col sm={4}>
              <TextInputField
                label="City"
                name="CityOrTownOrDistrict"
                value={values.CityOrTownOrDistrict}
                onChange={(value) => setFieldValue("CityOrTownOrDistrict", value)}
                onBlur={handleBlur("CityOrTownOrDistrict")}
                isInvalid={Boolean(errors.CityOrTownOrDistrict) && touched.CityOrTownOrDistrict}
                errorMsg={errors.CityOrTownOrDistrict}
              />
            </Col>
          </Row>
        </Col>
      </Row>
    </section>
  );
};

export default SalaryEmployerDetailsForm;
